// ONBOARDING-P3: the owner's review of the setup proposal, by the onboarding link. Once the intake
// is complete and the HTVA team sent a proposal (`onboarding.proposal.get` answers 200; 404 means
// nothing to review yet), the setup assistant walks the owner through it seat by seat, like a
// conversation: what each seat does in its first 30 days and how it is measured, whether it is
// included now or comes with an upgrade, and Approve / Ask for a change / Not now per seat. It
// offers a call (a booking link when one is set). Client-facing: only the view's plain words
// (no internal agent names, template keys, evidence or ids).
import { ApiError, type OperationOutput } from "@companyos/sdk";
import { Badge, Button, Label, Spinner, Textarea } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";

type View = OperationOutput<"onboarding.proposal.get">;
type Seat = View["seats"][number];
type Decision = "approve" | "change" | "not_now";

const proposalKey = (co: string, token: string) => ["onboarding-proposal", co, token] as const;

/** One message from the setup assistant. */
function Said({ from, children }: { from: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4">
      <p className="text-xs font-medium text-muted-foreground">{from}</p>
      {children}
    </div>
  );
}

function Recorded({ seat }: { seat: Seat }) {
  const d = seat.decision;
  if (!d) return null;
  const text =
    d.decision === "approve"
      ? "You approved this seat."
      : d.decision === "change"
        ? "You asked for a change."
        : "You said not now.";
  return (
    <div className="rounded-md bg-muted px-3 py-2 text-sm" data-slot="seat-decision">
      <p>{text}</p>
      {d.note ? <p className="text-muted-foreground">“{d.note}”</p> : null}
    </div>
  );
}

function SeatCard({
  seat,
  n,
  total,
  from,
  onDecide,
  busy,
  error,
}: {
  seat: Seat;
  n: number;
  total: number;
  from: string;
  onDecide: (decision: Decision, note?: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const [changing, setChanging] = useState(false);
  const [note, setNote] = useState("");
  const [needNote, setNeedNote] = useState(false);
  const noteId = `seat-note-${seat.key}`;
  return (
    <section aria-label={seat.title} data-seat={seat.key}>
      <Said from={from}>
        <p className="text-xs text-muted-foreground">
          Seat {n} of {total}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-heading text-lg font-semibold">{seat.title}</h2>
          <Badge variant={seat.start === "now" ? "success" : "info"}>
            {seat.start === "now" ? "Included now" : "On an upgrade"}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          {seat.kind === "de" ? "A digital employee" : "A person on your team"}
          {seat.helpers.length ? `, working with ${seat.helpers.join(", ")}` : ""}.
        </p>
        {seat.first_goals.length ? (
          <div>
            <p className="text-sm font-medium">In the first 30 days</p>
            <ul className="list-disc pl-5 text-sm">
              {seat.first_goals.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {seat.kpis.length ? (
          <div>
            <p className="text-sm font-medium">How we will measure it</p>
            <ul className="list-disc pl-5 text-sm">
              {seat.kpis.map((k) => (
                <li key={k}>{k}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="text-sm">Is this right for you?</p>
        <Recorded seat={seat} />
        {changing ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={noteId}>What would you like changed?</Label>
            <Textarea
              id={noteId}
              rows={3}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setNeedNote(false);
              }}
            />
            {needNote ? (
              <p role="alert" className="text-sm text-destructive">
                Tell us what you would like changed, so we can get it right.
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={busy}
                onClick={() => {
                  if (!note.trim()) {
                    setNeedNote(true);
                    return;
                  }
                  onDecide("change", note.trim());
                  setChanging(false);
                  setNote("");
                }}
              >
                Send the change request
              </Button>
              <Button variant="ghost" onClick={() => setChanging(false)}>
                Back
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => onDecide("approve")}>
              Approve
            </Button>
            <Button variant="secondary" disabled={busy} onClick={() => setChanging(true)}>
              Ask for a change
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => onDecide("not_now")}>
              Not now
            </Button>
          </div>
        )}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </Said>
    </section>
  );
}

const friendly = (e: unknown) =>
  e instanceof ApiError && e.status === 409
    ? "This proposal was updated in the meantime. Reload the page to see the latest version."
    : e instanceof ApiError && e.status === 422
      ? "That answer could not be saved. Please add a short note and try again."
      : "That could not be saved. Check your connection and try again.";

/**
 * The proposal review, or `fallback` when there is nothing to review yet (404) or it could not be
 * loaded.
 */
export function ProposalReview({
  api,
  co,
  token,
  fallback,
}: {
  api: Api;
  co: string;
  token: string;
  fallback: ReactNode;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: proposalKey(co, token),
    queryFn: () => api.client.onboarding.proposal.get({ params: { co, token } }),
    retry: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const decide = useMutation({
    mutationFn: (v: { seat_key: string; decision: Decision; note?: string }) =>
      api.client.onboarding.proposal.decideSeat({
        params: { co, token, seat_key: v.seat_key },
        body: { decision: v.decision, ...(v.note ? { note: v.note } : {}) },
      }),
    onSuccess: (v, vars) => {
      qc.setQueryData(proposalKey(co, token), v);
      setErrors((e) => ({ ...e, [vars.seat_key]: "" }));
    },
    onError: (e, vars) => setErrors((x) => ({ ...x, [vars.seat_key]: friendly(e) })),
  });
  if (q.isPending)
    return (
      <div className="flex justify-center py-16" role="status" aria-label="Loading">
        <Spinner />
      </div>
    );
  if (q.isError || !q.data) return <>{fallback}</>;
  const v = q.data;
  const answered = v.seats.filter((s) => s.decision).length;
  return (
    <div className="flex flex-col gap-4" data-slot="proposal-review">
      <Said from={v.from}>
        <h1 className="font-heading text-xl font-semibold">Your setup proposal</h1>
        {v.summary ? <p className="text-sm whitespace-pre-line">{v.summary}</p> : null}
        <p className="text-sm">
          For {v.company_name} we recommend starting with{" "}
          {v.seats.length === 1 ? "this seat" : `these ${v.seats.length} seats`}. Let's go through
          them one by one: tell us if each is right for you.
        </p>
        <p className="text-xs text-muted-foreground" data-slot="review-progress">
          {answered} of {v.seats.length} answered
        </p>
      </Said>
      {v.seats.map((s, i) => (
        <SeatCard
          key={s.key}
          seat={s}
          n={i + 1}
          total={v.seats.length}
          from={v.from}
          busy={decide.isPending}
          error={errors[s.key] || null}
          onDecide={(decision, note) =>
            decide.mutate({ seat_key: s.key, decision, ...(note ? { note } : {}) })
          }
        />
      ))}
      {v.first_goals.length ? (
        <Said from={v.from}>
          <p className="text-sm font-medium">What we aim for together in the first 30 days</p>
          <ul className="list-disc pl-5 text-sm">
            {v.first_goals.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </Said>
      ) : null}
      {v.open_questions.length ? (
        <Said from={v.from}>
          <p className="text-sm font-medium">A few questions for you</p>
          <ul className="list-disc pl-5 text-sm">
            {v.open_questions.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </Said>
      ) : null}
      <Said from={v.from}>
        <p className="text-sm">
          Would you rather talk it through? We are happy to go over it on a call.
        </p>
        {v.call.booking_url ? (
          <a
            href={v.call.booking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            Book a call
          </a>
        ) : (
          <p className="text-sm">Reply to us with a couple of times that suit you.</p>
        )}
      </Said>
    </div>
  );
}
